import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

// Drives the POS "New Sale" screen: clicking a product card adds (or
// increments) a cart row, totals/tax/balance recalculate live, payment rows
// can be added/removed, and a handful of keyboard shortcuts speed up
// cashier workflows. All cart/payment rows are plain `<tr>`/`<div>` elements
// with hidden inputs named for Rails nested attributes
// (`sale[sale_items_attributes][N][...]`, `sale[payments_attributes][N][...]`)
// so the whole cart submits as a normal form post.
export default class extends Controller {
  static targets = [
    "search", "cartBody", "itemTemplate", "emptyRow",
    "subtotal", "taxAmount", "totalAmount",
    "paymentsBody", "paymentTemplate", "amountPaid", "balanceDue",
    "saleType", "submit", "hold", "customer", "customerForm", "customerError"
  ]

  static values = {
    taxRate: { type: Number, default: 0 },
    prefill: { type: Array, default: [] }
  }

  connect() {
    this.itemIndex = 0
    this.paymentIndex = 0
    this.activeCategory = "all"

    this.prefillValue.forEach((item) => {
      this.appendItemRow({
        productId: item.product_id,
        variantId: item.product_variant_id,
        name: item.name,
        imageUrl: item.image_url,
        retailPrice: item.retail_price ?? item.unit_price,
        wholesalePrice: item.wholesale_price ?? item.unit_price,
        bulkPrice: item.bulk_price ?? item.unit_price,
        quantity: item.quantity,
        unitPrice: item.unit_price
      })
    })

    this.recalcTotals()

    this.keydownHandler = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.keydownHandler)
  }

  disconnect() {
    document.removeEventListener("keydown", this.keydownHandler)
  }

  // Product grid -----------------------------------------------------------

  addItem(event) {
    this.addItemFromCard(event.currentTarget)
  }

  addItemFromCard(card) {
    const productId = card.dataset.productId
    const variantId = card.dataset.variantId || ""

    const existing = this.cartBodyTarget.querySelector(
      `tr[data-product-id="${productId}"][data-variant-id="${variantId}"]`
    )

    if (existing) {
      const qtyInput = existing.querySelector("[data-field='quantity']")
      qtyInput.value = (parseInt(qtyInput.value || "0", 10) + 1).toString()
    } else {
      const saleType = this.hasSaleTypeTarget ? this.saleTypeTarget.value : "retail"
      const price = this.priceForType(card.dataset, saleType)

      this.appendItemRow({
        productId: productId,
        variantId: variantId,
        name: card.dataset.name,
        imageUrl: card.dataset.imageUrl,
        retailPrice: card.dataset.retailPrice,
        wholesalePrice: card.dataset.wholesalePrice,
        bulkPrice: card.dataset.bulkPrice,
        quantity: 1,
        unitPrice: price
      })
    }

    this.playAddSound()
    this.recalcTotals()
  }

  // Barcode readers act like a keyboard: they type the barcode digits into
  // whatever's focused, then send Enter. If the search field's current
  // value exactly matches a product/variant's barcode, add it straight to
  // the cart instead of just leaving the grid filtered down to one card.
  handleSearchKeydown(event) {
    if (event.key !== "Enter") return

    const query = this.searchTarget.value.trim().toLowerCase()
    if (query.length === 0) return

    const card = this.element.querySelector(`.pos-product-card[data-barcode="${CSS.escape(query)}"]`)
    if (!card) return

    event.preventDefault()
    this.addItemFromCard(card)
    this.searchTarget.value = ""
    this.filterProducts()
  }

  // Plays a short confirmation beep when an item is added to the cart.
  // Synthesized with the Web Audio API so no audio asset needs shipping.
  playAddSound() {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext
    if (!AudioContextClass) return

    this.audioContext ||= new AudioContextClass()
    const context = this.audioContext
    const oscillator = context.createOscillator()
    const gain = context.createGain()

    oscillator.type = "sine"
    oscillator.frequency.value = 880
    gain.gain.setValueAtTime(0.15, context.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.15)

    oscillator.connect(gain)
    gain.connect(context.destination)

    oscillator.start()
    oscillator.stop(context.currentTime + 0.15)
  }

  appendItemRow({ productId, variantId, name, imageUrl, retailPrice, wholesalePrice, bulkPrice, quantity, unitPrice }) {
    const index = this.itemIndex++
    const html = this.itemTemplateTarget.innerHTML.replaceAll("NEW_INDEX", index)
    const template = document.createElement("template")
    template.innerHTML = html.trim()
    const row = template.content.firstElementChild

    row.dataset.productId = productId
    row.dataset.variantId = variantId || ""
    row.dataset.retailPrice = retailPrice || "0"
    row.dataset.wholesalePrice = wholesalePrice || "0"
    row.dataset.bulkPrice = bulkPrice || "0"

    row.querySelector("[data-role='name']").textContent = name

    if (imageUrl) {
      const image = row.querySelector("[data-role='image']")
      image.src = imageUrl
      image.classList.remove("d-none")
    }
    row.querySelector("[data-field='product_id']").value = productId
    row.querySelector("[data-field='product_variant_id']").value = variantId || ""
    row.querySelector("[data-field='quantity']").value = quantity
    row.querySelector("[data-field='unit_price']").value = parseFloat(unitPrice || "0").toFixed(2)

    this.cartBodyTarget.appendChild(row)
  }

  removeItem(event) {
    event.currentTarget.closest("tr").remove()
    this.recalcTotals()
  }

  changeSaleType() {
    const saleType = this.saleTypeTarget.value

    this.cartBodyTarget.querySelectorAll("tr[data-product-id]").forEach((row) => {
      const price = this.priceForType(row.dataset, saleType)
      row.querySelector("[data-field='unit_price']").value = parseFloat(price || "0").toFixed(2)
    })

    this.recalcTotals()
  }

  // Looks up the price for a given sale type ("retail"/"wholesale"/"bulk")
  // from a dataset carrying retailPrice/wholesalePrice/bulkPrice, falling
  // back to the retail price if the requested tier isn't set.
  priceForType(dataset, type) {
    return dataset[`${type}Price`] || dataset.retailPrice || "0"
  }

  filterProducts() {
    const query = this.searchTarget.value.toLowerCase().trim()
    const category = this.activeCategory || "all"

    this.element.querySelectorAll(".product-card").forEach((card) => {
      const haystack = card.dataset.posSearch || ""
      const matchesSearch = query.length === 0 || haystack.includes(query)
      const matchesCategory = category === "all" || card.dataset.categoryId === category
      card.classList.toggle("d-none", !(matchesSearch && matchesCategory))
    })
  }

  filterCategory(event) {
    const button = event.currentTarget
    this.activeCategory = button.dataset.category

    this.element.querySelectorAll("[data-pos-category-pill]").forEach((pill) => {
      pill.classList.toggle("active", pill === button)
    })

    this.filterProducts()
  }

  // Payments -----------------------------------------------------------------

  addPayment() {
    const index = this.paymentIndex++
    const html = this.paymentTemplateTarget.innerHTML.replaceAll("NEW_PAY_INDEX", index)
    const template = document.createElement("template")
    template.innerHTML = html.trim()
    this.paymentsBodyTarget.appendChild(template.content.firstElementChild)
    this.recalcTotals()
  }

  removePayment(event) {
    event.currentTarget.closest(".payment-row").remove()
    this.recalcTotals()
  }

  // Totals ---------------------------------------------------------------

  recalc() {
    this.recalcTotals()
  }

  recalcTotals() {
    let subtotal = 0
    let taxAmount = 0

    const itemRows = this.cartBodyTarget.querySelectorAll("tr[data-product-id]")

    if (this.hasEmptyRowTarget) {
      this.emptyRowTarget.classList.toggle("d-none", itemRows.length > 0)
    }

    itemRows.forEach((row) => {
      const qty = parseFloat(row.querySelector("[data-field='quantity']").value || "0")
      const price = parseFloat(row.querySelector("[data-field='unit_price']").value || "0")
      const lineTotal = qty * price
      row.querySelector("[data-role='lineTotal']").textContent = lineTotal.toFixed(2)
      subtotal += lineTotal
      // Round each line's tax individually, the same way the server computes and
      // rounds SaleItem#tax_amount per item, so the two totals never drift apart
      // by a cent or two on multi-item carts (most visible on resumed held sales).
      taxAmount += this.round2(lineTotal * (this.taxRateValue / 100))
    })

    const total = subtotal + taxAmount

    this.subtotalTarget.textContent = subtotal.toFixed(2)
    this.taxAmountTarget.textContent = taxAmount.toFixed(2)
    this.totalAmountTarget.textContent = total.toFixed(2)

    let paid = 0
    this.paymentsBodyTarget.querySelectorAll("[data-field='amount']").forEach((input) => {
      paid += parseFloat(input.value || "0")
    })

    this.amountPaidTarget.textContent = paid.toFixed(2)
    this.balanceDueTarget.textContent = Math.max(total - paid, 0).toFixed(2)

    this.currentTotal = total
    this.currentPaid = paid
  }

  round2(value) {
    return Math.round((value + Number.EPSILON) * 100) / 100
  }

  // New customer dialog --------------------------------------------------------

  // Creates a customer without leaving the POS, then adds it to the customer
  // dropdown and selects it for the current sale.
  async createCustomer(event) {
    event.preventDefault()

    const form = this.customerFormTarget
    const errorBox = this.customerErrorTarget
    errorBox.classList.add("d-none")

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content

    try {
      const response = await fetch(form.action, {
        method: "POST",
        headers: { "Accept": "application/json", "X-CSRF-Token": csrfToken },
        body: new FormData(form)
      })
      const data = await response.json()

      if (!response.ok) {
        errorBox.textContent = (data.errors || ["Could not save the customer."]).join(", ")
        errorBox.classList.remove("d-none")
        return
      }

      const option = new Option(data.name, data.id, true, true)
      this.customerTarget.add(option)
      this.customerTarget.value = data.id

      form.reset()
      const modalElement = form.closest(".modal")
      Modal.getOrCreateInstance(modalElement).hide()
    } catch (error) {
      errorBox.textContent = "Could not save the customer. Check your connection and try again."
      errorBox.classList.remove("d-none")
    }
  }

  // Puts a number from the calculator into a payment amount (the first empty
  // payment row, or a new one).
  applyCalculatorAmount(event) {
    const value = Number(event.detail.value)
    if (!Number.isFinite(value) || value < 0) return

    const rows = [...this.paymentsBodyTarget.querySelectorAll(".payment-row")]
    let row = rows.reverse().find((r) => !parseFloat(r.querySelector("[data-field='amount']").value || "0"))

    if (!row) {
      this.addPayment()
      row = this.paymentsBodyTarget.querySelector(".payment-row:last-child")
    }

    row.querySelector("[data-field='amount']").value = value.toFixed(2)
    this.recalcTotals()
  }

  // Submit guards ------------------------------------------------------------

  // Blank/zero payment rows would be rejected by the server, so drop them
  // before the form is submitted.
  pruneEmptyPayments() {
    this.paymentsBodyTarget.querySelectorAll(".payment-row").forEach((row) => {
      const amount = parseFloat(row.querySelector("[data-field='amount']").value || "0")
      if (!(amount > 0)) row.remove()
    })
    this.recalcTotals()
  }

  hasCartItems() {
    return this.cartBodyTarget.querySelectorAll("tr[data-product-id]").length > 0
  }

  // Holding needs at least one product; payments entered so far are not kept.
  validateHold(event) {
    if (!this.hasCartItems()) {
      event.preventDefault()
      alert("Add at least one product before holding the sale.")
    }
  }

  validateComplete(event) {
    if (!this.hasCartItems()) {
      event.preventDefault()
      alert("Add at least one product to the sale.")
      return
    }

    this.pruneEmptyPayments()

    if (this.currentPaid + 0.005 < this.currentTotal) {
      event.preventDefault()
      alert("This sale is not fully paid. Add payments that cover the total, or use Sell on Credit to keep the balance as an installment.")
    }
  }

  validateCredit(event) {
    if (!this.hasCartItems()) {
      event.preventDefault()
      alert("Add at least one product to the sale.")
      return
    }

    if (!this.customerTarget.value) {
      event.preventDefault()
      alert("Select a customer to sell on credit.")
      return
    }

    this.pruneEmptyPayments()

    if (this.currentPaid + 0.005 >= this.currentTotal) {
      event.preventDefault()
      alert("A credit sale needs an unpaid balance. Use Complete Sale when the sale is fully paid.")
    }
  }

  // Keyboard shortcuts -----------------------------------------------------

  handleKeydown(event) {
    if (event.key === "F2") {
      event.preventDefault()
      this.searchTarget.focus()
    } else if (event.key === "F9") {
      event.preventDefault()
      this.submitTarget.click()
    } else if (event.key === "F4") {
      event.preventDefault()
      const calculator = document.getElementById("calculator-modal")
      if (calculator) Modal.getOrCreateInstance(calculator).toggle()
    } else if (event.key === "F6") {
      event.preventDefault()
      this.holdTarget.click()
    } else if (event.key === "F8") {
      event.preventDefault()
      this.addPayment()
    } else if (event.key === "Escape" && document.activeElement === this.searchTarget) {
      this.searchTarget.value = ""
      this.filterProducts()
    }
  }
}
